import { useEffect, useId, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

/**
 * ONE fold for every tall control area on a phone (owner, 2026-09-23: "the
 * categories overfill the whole screen and we cannot see the data, there
 * should be collapse buttons on every dropdown menu").
 *
 * Wrap the area — a filter toolbar, a strip of category chips, a grouped
 * menu — and on a phone it becomes one button that says what is inside
 * ("Filters", "Categories", the current settings page), how many filters are
 * active while it is shut, and a chevron. Open, the controls drop down under
 * it at full width; shut, the data gets the screen.
 *
 * "A phone" is the `compact` variant (styles/responsive.css): narrower than
 * `md`, or a touch screen on its side. From there up the button does not
 * exist and the panel renders `display: contents`, so the children lay out
 * in the parent exactly as if this component were not there — zero desktop
 * delta by construction, which is what lets it be dropped into a page
 * without re-checking the desktop layout. `always` opts out of that and
 * folds at every size, for a list that is tall on a monitor too.
 *
 * `storageKey` remembers open/shut per browser (one key per page and area,
 * e.g. `devices.filters`). Omit it for a fold that should start shut every
 * visit, like a navigation menu.
 */

/** Everything stored by this component shares this prefix. */
export const COLLAPSIBLE_STORAGE_PREFIX = 'gc.staff.collapse.';

/** The storage slice this needs — `localStorage`, or a fake in tests. */
export type CollapsibleStorage = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): CollapsibleStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Some locked-down browsers throw on the bare property access.
    return null;
  }
}

/**
 * The remembered state for `key`, or `fallback` when there is none or
 * storage refuses. Every failure mode lands on the fallback: a fold is a
 * convenience, and it must never be the thing that breaks a page.
 */
export function readCollapsibleOpen(
  key: string | undefined,
  fallback: boolean,
  storage: CollapsibleStorage | null = defaultStorage(),
): boolean {
  if (!key || !storage) return fallback;
  try {
    const raw = storage.getItem(COLLAPSIBLE_STORAGE_PREFIX + key);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return fallback;
  } catch {
    return fallback;
  }
}

export function writeCollapsibleOpen(
  key: string | undefined,
  open: boolean,
  storage: CollapsibleStorage | null = defaultStorage(),
): void {
  if (!key || !storage) return;
  try {
    storage.setItem(COLLAPSIBLE_STORAGE_PREFIX + key, open ? '1' : '0');
  } catch {
    // Private mode or a full quota: the fold just won't survive a reload.
  }
}

/**
 * A per-PAGE key for callers that have no name of their own for their area
 * (the older `FilterDisclosure` call sites): the path the page was opened on.
 * Read once, at mount — the fold belongs to the page it was drawn on.
 */
export function pageStorageKey(area: string): string {
  const path = typeof window === 'undefined' ? '' : window.location.pathname;
  return `${area}@${path || '/'}`;
}

export interface CollapsibleProps {
  /** What the button says: "Filters", "Categories", a page name. */
  label: ReactNode;
  /** A smaller line above the label — e.g. "Settings" over the page name. */
  caption?: ReactNode;
  /** What is chosen inside, shown on the button while shut ("Drinks"). */
  summary?: ReactNode;
  /** Active filters hidden inside. A badge, so a short list has a visible reason. */
  count?: number;
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }> | null;
  /** Remember open/shut per browser under this key. */
  storageKey?: string;
  defaultOpen?: boolean;
  /** Controlled use: the caller owns the state (e.g. to shut it on navigation). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Shut the panel whenever this value changes — pass `location.key` to fold a
   * menu away once it has taken the user somewhere.
   */
  collapseOn?: unknown;
  /** Fold at every screen size, not only on a phone. */
  always?: boolean;
  /**
   * How the open panel lays its children out on a phone: `stack` is one
   * control per full-width line (selects, date fields); `wrap` flows chips
   * into rows; `block` leaves the children to lay themselves out.
   */
  layout?: 'stack' | 'wrap' | 'block';
  /**
   * Cap the open panel at ~45% of the visible height (30% on a phone on its
   * side) and scroll inside it, so an open list of forty chips still leaves
   * the data on screen. Default on for `wrap`, off otherwise — a cap would
   * clip a popover inside a stack.
   */
  cap?: boolean;
  /** The button stretches the full width of its row (menus). */
  block?: boolean;
  /** Extra classes for the panel while it is a real box (phone, or `always`). */
  panelClassName?: string;
  children: ReactNode;
}

const PANEL_LAYOUT: Record<NonNullable<CollapsibleProps['layout']>, string> = {
  // A sideways phone is 800px wide: one select per line there is a waste of
  // the one dimension it has plenty of, so from `md` the stack wraps instead.
  stack: 'flex-col items-stretch gap-2 md:flex-row md:flex-wrap md:items-center',
  wrap: 'flex-wrap items-center gap-2',
  block: 'flex-col',
};

/**
 * Pure markup, state passed in — what the render tests pin, since this
 * workspace has no DOM to click in.
 */
export function CollapsibleView({
  open,
  onToggle,
  panelId,
  label,
  caption,
  summary,
  count = 0,
  icon: Icon,
  always = false,
  layout = 'stack',
  cap,
  block = false,
  panelClassName = '',
  children,
}: Omit<CollapsibleProps, 'storageKey' | 'defaultOpen' | 'onOpenChange' | 'collapseOn'> & {
  open: boolean;
  onToggle: () => void;
  panelId: string;
}) {
  const { t } = useTranslation();
  const capped = cap ?? layout === 'wrap';

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        data-collapsible-toggle
        className={[
          'min-h-10 items-center gap-2 rounded-control border bg-input px-3 text-start text-sm text-content transition-colors hover:bg-hover',
          block ? 'flex w-full' : 'inline-flex max-w-full',
          // Lit while it hides something that changes the list.
          count > 0 ? 'border-primary/60' : 'border-subtle',
          always ? '' : 'roomy:hidden',
        ].join(' ')}
      >
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-content-secondary" aria-hidden /> : null}
        <span className={`min-w-0 ${block ? 'flex-1' : ''}`}>
          {caption ? (
            <span className="block text-[10px] uppercase tracking-[0.18em] text-content-muted">
              {caption}
            </span>
          ) : null}
          <span className="block truncate font-medium">
            {label}
            {!open && summary ? (
              <span className="font-normal text-content-secondary"> · {summary}</span>
            ) : null}
          </span>
        </span>
        {count > 0 ? (
          <span
            className="tabular grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-primary px-1 text-[11px] font-bold text-primary-fg"
            aria-label={t('layout.collapsible.active', { count })}
          >
            {count}
          </span>
        ) : null}
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-content-secondary transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      {/* `w-full` puts the panel on its own line of a wrapping toolbar. From
          the roomy breakpoint it is `display: contents` — no box at all — so
          the children sit in the parent's flex row as they always did. */}
      <div
        id={panelId}
        data-collapsible-panel
        data-open={open ? 'true' : 'false'}
        className={[
          open ? 'flex' : 'hidden',
          'w-full',
          PANEL_LAYOUT[layout],
          capped ? 'gc-collapse-cap' : '',
          panelClassName,
          always ? '' : 'roomy:contents',
        ].join(' ')}
      >
        {children}
      </div>
    </>
  );
}

/**
 * The fold with its state: remembered per `storageKey`, optionally controlled,
 * optionally shut by `collapseOn`.
 */
export function Collapsible({
  storageKey,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  collapseOn,
  ...view
}: CollapsibleProps) {
  const panelId = useId();
  const [ownOpen, setOwnOpen] = useState(() => readCollapsibleOpen(storageKey, defaultOpen));
  const open = controlledOpen ?? ownOpen;

  function setOpen(next: boolean): void {
    if (controlledOpen === undefined) setOwnOpen(next);
    writeCollapsibleOpen(storageKey, next);
    onOpenChange?.(next);
  }

  // Compared against the last value seen, not "skip the first run": StrictMode
  // runs a mount effect twice, and a first-run flag would read the second run
  // as a change and shut a fold the user had left open.
  const lastCollapseOn = useRef(collapseOn);
  useEffect(() => {
    if (Object.is(lastCollapseOn.current, collapseOn)) return;
    lastCollapseOn.current = collapseOn;
    if (controlledOpen === undefined) setOwnOpen(false);
    onOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fold on the signal only
  }, [collapseOn]);

  return (
    <CollapsibleView {...view} open={open} onToggle={() => setOpen(!open)} panelId={panelId} />
  );
}
